/**
 * EL SIMULADOR PASO A PASO (reunión con César, 30-09).
 *
 * La tabla de los clientes de prueba (dos por plan) con el reloj de la app corriendo sobre una
 * hora ficticia: «si le pongo miércoles a las 8:59, que en un minuto, a las 9, me llegue el
 * correo», «poderlos pasar de ciclo», «que los datos vayan cambiándose semana a semana».
 * El backend (core/simulador_reloj) no toca la base: la tabla viaja en cada petición y se
 * guarda en este navegador para seguir donde se dejó.
 *
 * Desde el 08-10 cada paso enseña el texto de los correos que saldrían y, con la casilla
 * marcada (la pinta la página solo donde el servidor lo permite), los manda de prueba.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { FastForward, RotateCcw, Search, Clock } from 'lucide-react';
import { PlanBadge } from '../pages/ClientDashboard';
import { mensajeDeError } from '../lib/mensajeDeError';
import { agruparCambios, detallesEnLinea } from '../lib/cambiosDelReloj';
import { correosDelPaso, filasParaAvanzar, seGastaLaCasilla, sinElCorreo } from '../lib/correosDelSimulador';
import { CasillaDeCorreo, EnvioDePrueba, TextoDelCorreo } from './CorreosDelSimulador';

// Campos que esta pantalla nunca ha enseñado en los cambios del camino de antes.
const OCULTOS = ['hasta', 'listo_para_4', 'ciclo_reinicia', 'semana0_desde'];

// Una tabla guardada por origen (01-10): la de prueba no pisa la de reales. Con «real» es la
// cartera activa con el reloj que tiene HOY, y solo hacia delante: su pasado no está guardado.
const clave = (origen) => (origen === 'real' ? 'simulador-paso-a-paso-reales' : 'simulador-paso-a-paso');
const guardadoDe = (origen) => { try { return JSON.parse(localStorage.getItem(clave(origen)) || 'null'); } catch { return null; } };
const guardarDe = (origen, v) => { try { localStorage.setItem(clave(origen), JSON.stringify(v)); } catch { /* sin almacenamiento */ } };

// La hora ficticia se escribe y se lee en hora de España.
const partes = (iso) => {
    const d = new Date(iso);
    const f = (o) => new Intl.DateTimeFormat('es-ES', { timeZone: 'Europe/Madrid', ...o }).format(d);
    return {
        largo: f({ weekday: 'long', day: 'numeric', month: 'long' }),
        hora: f({ hour: '2-digit', minute: '2-digit' }),
        corto: f({ weekday: 'short', day: '2-digit', month: '2-digit' }),
    };
};
const aIsoMadrid = (dia, hora) => `${dia}T${hora}:00`;       // el backend lo lee como hora de España
const sumar = (iso, minutos) => new Date(new Date(iso).getTime() + minutos * 60000).toISOString();

const ESTADO = {
    ENVIADO: 'enviado', 'ENVIADO RECORDATORIO DOMINGO': 'recordatorio del domingo',
    'ENVIADO RECORDATORIO LUNES': 'recordatorio del lunes', 'NO ENVIADO': 'no llegó',
    RELLENADO: 'rellenado', CERRADO: 'cerrado',
};
const estado = (v) => (v ? ESTADO[v] || v.toLowerCase() : '·');

const SimuladorPasoAPaso = ({ origen = 'prueba', correoDePrueba = null, mandarCorreos = false, onMandarCorreos = () => {} }) => {
    const { api } = useAuth();
    const reales = origen === 'real';
    const guardado = () => guardadoDe(origen);
    const guardar = (v) => guardarDe(origen, v);
    const [tabla, setTabla] = useState(guardado);
    const [historia, setHistoria] = useState(() => (guardado()?.historia || []));
    const [trabajando, setTrabajando] = useState(false);
    const [buscar, setBuscar] = useState('');
    const [soloConCambios, setSoloConCambios] = useState(false);
    const [dia, setDia] = useState('');
    const [hora, setHora] = useState('');
    const [aMano, setAMano] = useState(null);
    // Cómo fue el envío de prueba del último paso. No se guarda: al recargar no hay ninguno.
    const [envio, setEnvio] = useState(null);
    const conCorreos = () => (mandarCorreos && correoDePrueba?.se_puede ? { mandar_correos: true } : {});
    // Lo que el servidor añade a su respuesta sobre el correo no es parte de la tabla ni de la fila.
    const sinLoDelCorreo = (datos) => {
        const { correos_mandados, correo_de_prueba, ...resto } = datos;
        setEnvio(correos_mandados || null);
        // La casilla vale para un envío: quien quiera otro, la vuelve a marcar.
        if (seGastaLaCasilla(correos_mandados)) onMandarCorreos(false);
        return resto;
    };
    // SIEMPRE LA TABLA ÚLTIMA: cada paso sale de la anterior. Leída del estado de React, un
    // «Hasta el próximo cambio» pulsado antes de que volviera «Manda el mensual» avanzaba la
    // tabla vieja y la acción se perdía (probado en el navegador, 01-10).
    const tablaRef = useRef(tabla);
    const historiaRef = useRef(historia);

    const poner = (t, eventos = []) => {
        const nueva = [...eventos, ...historiaRef.current].slice(0, 300);
        tablaRef.current = t;
        historiaRef.current = nueva;
        setTabla(t);
        setHistoria(nueva);
        guardar({ ...t, historia: nueva });
    };

    const eventosDe = (t) => t.filas.flatMap((f) => (f.cambios || []).map((c) => ({ ...sinElCorreo(c), nombre: f.nombre, cliente_id: f.id })))
        .filter((c) => !OCULTOS.includes(c.campo));

    const pedir = async (ruta, cuerpo, conEventos = true) => {
        setTrabajando(true);
        try {
            const r = await api.post(`/admin/simulador-semanas/reloj/${ruta}`, cuerpo);
            const t = sinLoDelCorreo(r.data);
            poner(t, conEventos ? eventosDe(t) : []);
            return t;
        } catch (e) {
            console.error('[simulador paso a paso]', e);
            toast.error(mensajeDeError(e, 'No se pudo mover la tabla'));
            return null;
        } finally {
            setTrabajando(false);
        }
    };

    const empezar = (inicio) => {
        historiaRef.current = [];
        setHistoria([]);
        return pedir('iniciar', { origen, ...(inicio ? { inicio } : {}) }, false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- solo al entrar
    useEffect(() => { if (!tabla) empezar(); }, []);

    const avanzarHasta = (hasta) => pedir('avanzar', { filas: filasParaAvanzar(tablaRef.current.filas), hasta, ...conCorreos() });
    const siguiente = async () => {
        setTrabajando(true);
        const r = await api.post('/admin/simulador-semanas/reloj/siguiente', { desde: tablaRef.current.instante }).catch(() => null);
        if (r) await avanzarHasta(r.data.instante); else setTrabajando(false);
    };
    const irA = () => {
        if (!dia || !hora) return;
        const destino = aIsoMadrid(dia, hora);
        // Hacia atrás no se puede correr un reloj: se empieza de nuevo en esa hora.
        // Con clientes reales no hay «de nuevo» en el pasado: su reloj parte de hoy.
        if (new Date(`${destino}+02:00`) < new Date(tablaRef.current.instante)) {
            if (reales) toast.error('Con clientes reales no se puede volver atrás: la tabla parte de hoy.');
            else empezar(destino);
        } else avanzarHasta(destino);
    };

    const cambiarFila = (nueva) => {
        const actual = tablaRef.current;
        const t = { ...actual, filas: actual.filas.map((f) => (f.id === nueva.id ? nueva : f)) };
        poner(t, eventosDe({ filas: [nueva] }));
    };
    const deLaTabla = (id) => tablaRef.current.filas.find((f) => f.id === id);
    const accion = async (f, que, extra = {}) => {
        setTrabajando(true);
        try {
            const r = await api.post('/admin/simulador-semanas/reloj/entrada',
                { fila: deLaTabla(f.id), que, cuando: tablaRef.current.instante, ...extra, ...conCorreos() });
            cambiarFila(sinLoDelCorreo(r.data));
        } catch (e) {
            console.error('[simulador paso a paso]', e);
            toast.error('No se pudo simular esa acción');
        } finally {
            setTrabajando(false);
        }
    };
    const fijar = async () => {
        setTrabajando(true);
        try {
            const r = await api.post('/admin/simulador-semanas/reloj/fijar',
                { fila: deLaTabla(aMano.fila.id), cuando: tablaRef.current.instante, semana: aMano.semana, ciclo: aMano.ciclo, ...conCorreos() });
            cambiarFila(sinLoDelCorreo(r.data));
            setAMano(null);
        } catch (e) {
            console.error('[simulador paso a paso]', e);
            toast.error('No se pudo poner esa semana');
        } finally {
            setTrabajando(false);
        }
    };

    const filas = useMemo(() => {
        const q = buscar.trim().toLowerCase();
        return (tabla?.filas || [])
            .filter((f) => !q || `${f.nombre} ${f.codigo}`.toLowerCase().includes(q))
            .filter((f) => !soloConCambios || (f.cambios || []).length);
    }, [tabla, buscar, soloConCambios]);

    const correos = useMemo(() => correosDelPaso(tabla?.filas), [tabla]);

    if (!tabla) return <p className="text-white/50 p-4">Preparando la tabla…</p>;
    const ahora = partes(tabla.instante);

    return (
        <div className="space-y-5 min-w-0" data-testid="simulador-paso">
            <Card className="bg-[#111] border-[#FF671F]/40">
                <CardContent className="p-4 sm:p-5 space-y-4">
                    <div className="flex flex-wrap items-end justify-between gap-4">
                        <div>
                            <p className="text-xs uppercase tracking-wider text-[#FF671F] flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" />Hora ficticia (España)</p>
                            <p className="text-2xl sm:text-3xl text-white font-bold first-letter:uppercase" style={{ fontFamily: 'Barlow Condensed' }} data-testid="paso-hora">
                                {ahora.largo}, {ahora.hora}
                            </p>
                        </div>
                        <div className="flex flex-wrap items-end gap-2">
                            <label className="text-xs text-white/50">Día
                                <input type="date" value={dia} onChange={(e) => setDia(e.target.value)}
                                    className="block mt-1 bg-[#0A0A0A] border border-[#2A2A2A] rounded-md px-2 py-1.5 text-sm text-white" />
                            </label>
                            <label className="text-xs text-white/50">Hora
                                <input type="time" value={hora} onChange={(e) => setHora(e.target.value)}
                                    className="block mt-1 bg-[#0A0A0A] border border-[#2A2A2A] rounded-md px-2 py-1.5 text-sm text-white" />
                            </label>
                            <Button onClick={irA} disabled={trabajando || !dia || !hora} data-testid="paso-ir"
                                className="bg-[#1F1F1F] hover:bg-[#2A2A2A] text-white h-9">Ir a esa hora</Button>
                        </div>
                    </div>
                    <div className="flex flex-wrap gap-2">
                        {[['+1 hora', 60], ['+1 día', 1440], ['+1 semana', 10080]].map(([t, min]) => (
                            <Button key={t} onClick={() => avanzarHasta(sumar(tabla.instante, min))} disabled={trabajando}
                                className="bg-[#1F1F1F] hover:bg-[#2A2A2A] text-white h-9 px-3 text-sm">{t}</Button>
                        ))}
                        <Button onClick={siguiente} disabled={trabajando} data-testid="paso-siguiente"
                            className="bg-[#FF671F] hover:bg-[#FF671F]/90 text-white h-9 px-3 text-sm font-bold">
                            <FastForward className="w-4 h-4 mr-1.5" />Hasta el próximo cambio
                        </Button>
                        <Button onClick={() => empezar()} disabled={trabajando}
                            className="bg-transparent border border-[#2A2A2A] hover:bg-[#1A1A1A] text-white/70 h-9 px-3 text-sm">
                            <RotateCcw className="w-4 h-4 mr-1.5" />Empezar de nuevo
                        </Button>
                    </div>
                    <CasillaDeCorreo donde={correoDePrueba} marcada={mandarCorreos} desactivada={trabajando} onCambio={onMandarCorreos} />
                    {reales && <p className="text-xs text-white/40">{tabla.filas.length} clientes activos</p>}
                </CardContent>
            </Card>

            <EnvioDePrueba envio={envio} />

            <Card className="bg-[#111] border-[#222] min-w-0">
                <CardContent className="p-4 sm:p-5">
                    <p className="text-xs uppercase tracking-wider text-white/40 mb-2">Correos de este paso</p>
                    {correos.length === 0 ? (
                        <p className="text-sm text-white/40">En el último paso no sale ningún correo.</p>
                    ) : (<>
                        <p className="text-xs text-white/40">
                            {correos.length === 1 ? 'Sale 1 correo' : `Salen ${correos.length} correos`}
                        </p>
                        <div className="max-h-[420px] overflow-auto pr-1" data-testid="paso-correos">
                            {correos.map((c, i) => (
                                <TextoDelCorreo key={i} correo={c} abierto={correos.length === 1} titulo={c.nombre} />
                            ))}
                        </div>
                    </>)}
                </CardContent>
            </Card>

            <div className="grid 2xl:grid-cols-4 gap-5 min-w-0">
                <Card className="bg-[#111] border-[#222] 2xl:col-span-3 min-w-0">
                    <CardContent className="p-4 sm:p-5">
                        <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
                            <label className="text-sm text-white/60 flex items-center gap-2">
                                <input type="checkbox" className="accent-[#FF671F]" checked={soloConCambios}
                                    onChange={(e) => setSoloConCambios(e.target.checked)} />
                                Solo los que han cambiado en el último paso
                            </label>
                            <div className="relative w-full sm:max-w-[240px]">
                                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-white/30" />
                                <input value={buscar} onChange={(e) => setBuscar(e.target.value)} placeholder="Buscar plan o cliente…"
                                    className="w-full bg-[#0A0A0A] border border-[#2A2A2A] rounded-lg pl-9 pr-3 py-2 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-[#FF671F]" />
                            </div>
                        </div>
                        <div className="overflow-auto max-h-[560px] rounded-lg border border-[#222] max-w-full">
                            <table className="w-full text-sm min-w-[900px]" data-testid="paso-tabla">
                                <thead className="sticky top-0 bg-[#141414] z-10">
                                    <tr className="text-left text-[11px] uppercase tracking-wider text-white/40">
                                        <th className="px-2.5 py-2.5 font-medium">Cliente</th>
                                        <th className="px-2.5 py-2.5 font-medium">Ciclo</th>
                                        <th className="px-2.5 py-2.5 font-medium">Semana</th>
                                        <th className="px-2.5 py-2.5 font-medium">Plan</th>
                                        <th className="px-2.5 py-2.5 font-medium">Quincenal</th>
                                        <th className="px-2.5 py-2.5 font-medium">Mensual</th>
                                        <th className="px-2.5 py-2.5 font-medium">En este paso</th>
                                        <th className="px-2.5 py-2.5 font-medium">Hacer</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filas.map((f) => {
                                        const r = f.resumen || {};
                                        // Una línea por regla, no una por campo (lib/cambiosDelReloj).
                                        const vis = agruparCambios(f.cambios, { ocultar: OCULTOS });
                                        return (
                                            <tr key={f.id} className={`border-t border-[#1E1E1E] align-top ${vis.length ? 'bg-[#FF671F]/5' : ''}`}>
                                                <td className="px-2.5 py-2.5">
                                                    <p className="text-white font-medium">{f.nombre}</p>
                                                    <div className="mt-1"><PlanBadge plan={f.codigo} /></div>
                                                </td>
                                                <td className="px-2.5 py-2.5 text-white/80 whitespace-nowrap">{r.ciclo ? `${r.ciclo}${r.ciclos ? ` de ${r.ciclos}` : ''}` : '·'}</td>
                                                <td className="px-2.5 py-2.5 text-white whitespace-nowrap font-semibold">{r.semana ?? '·'}{r.semana != null ? ` de ${r.num_semanas || 4}` : ''}{r.stop ? ' (parado)' : ''}</td>
                                                <td className="px-2.5 py-2.5 text-white/70 whitespace-nowrap">{r.semana_plan != null ? `${r.semana_plan}${r.plan_semanas ? ` de ${r.plan_semanas}` : ''}` : '·'}</td>
                                                <td className="px-2.5 py-2.5 text-white/70">{estado(r.reporte_quincenal)}</td>
                                                <td className="px-2.5 py-2.5 text-white/70">{estado(r.reporte_mensual)}</td>
                                                {/* Con su ancho (y el desplegable de «Hacer» con el suyo): sin él esta columna se
                                                    quedaba en 89 px y la fila medía 348 px, un hueco en blanco a 390 px. */}
                                                <td className="px-2.5 py-2.5 text-xs text-[#FF671F] min-w-[176px] max-w-[320px]">
                                                    {vis.map((g, i) => (
                                                        <p key={i}>{g.titulo}{g.detalles.length > 0 && <span className="text-white/50">{' · '}{detallesEnLinea(g)}</span>}</p>
                                                    ))}
                                                </td>
                                                <td className="px-2.5 py-2.5">
                                                    <select defaultValue="" data-testid={`paso-hacer-${f.id}`} disabled={trabajando}
                                                        onChange={(e) => {
                                                            const v = e.target.value; e.target.value = '';
                                                            if (v === 'mensual' || v === 'quincenal') accion(f, 'reporte', { tipo: v });
                                                            else if (v === 'cuestionario') accion(f, 'cuestionario');
                                                            else if (v === 'pago') accion(f, 'pago');
                                                            else if (v === 'pausa') accion(f, 'pausa', { puesta: !r.stop });
                                                            else if (v === 'mano') setAMano({ fila: f, semana: r.semana ?? 1, ciclo: r.ciclo ?? 1 });
                                                        }}
                                                        className="bg-[#0A0A0A] border border-[#2A2A2A] rounded-md px-2 py-1 text-xs text-white w-[150px]">
                                                        <option value="" disabled>Elegir…</option>
                                                        <option value="mensual">Manda el mensual</option>
                                                        <option value="quincenal">Manda el quincenal</option>
                                                        <option value="cuestionario">Rellena el cuestionario</option>
                                                        <option value="pago">Paga la renovación</option>
                                                        <option value="pausa">{r.stop ? 'Quitar la pausa' : 'Poner en pausa'}</option>
                                                        <option value="mano">Ponerlo en otra semana…</option>
                                                    </select>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </CardContent>
                </Card>

                <Card className="bg-[#111] border-[#222] min-w-0">
                    <CardContent className="p-4 sm:p-5">
                        <p className="text-xs uppercase tracking-wider text-white/40 mb-2">Lo que ha ido pasando</p>
                        {historia.length === 0 ? <p className="text-sm text-white/40">Avanza la hora y aquí se va apuntando cada cambio.</p> : (
                            <ul className="space-y-1.5 max-h-[320px] 2xl:max-h-[560px] overflow-auto pr-1" data-testid="paso-historia">
                                {agruparCambios(historia, { ocultar: OCULTOS }).map((h, i) => {
                                    const p = h.cuando ? partes(h.cuando) : null;
                                    return (
                                        <li key={i} className="text-xs">
                                            <span className="text-white/40">{p ? `${p.corto} ${p.hora}` : ''}</span>{' · '}
                                            <span className="text-white/85">{h.nombre}</span>{' · '}
                                            <span className="text-white/60">{h.titulo}</span>
                                            {h.detalles.length > 0 && <span className="text-white/40">{' · '}{detallesEnLinea(h)}</span>}
                                        </li>
                                    );
                                })}
                            </ul>
                        )}
                    </CardContent>
                </Card>
            </div>

            {aMano && (
                <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={() => setAMano(null)}>
                    <div className="bg-[#111] border border-[#2A2A2A] rounded-xl p-5 w-full max-w-sm space-y-3" onClick={(e) => e.stopPropagation()}>
                        <p className="text-white font-medium">Poner a {aMano.fila.nombre}</p>
                        <div className="grid grid-cols-2 gap-3">
                            <label className="text-xs text-white/50">Ciclo
                                <input type="number" min="1" max="9" value={aMano.ciclo} onChange={(e) => setAMano({ ...aMano, ciclo: e.target.value })}
                                    className="mt-1 w-full bg-[#0A0A0A] border border-[#2A2A2A] rounded-md px-2 py-1.5 text-sm text-white" />
                            </label>
                            <label className="text-xs text-white/50">Semana
                                <input type="number" min="0" max="6" value={aMano.semana} onChange={(e) => setAMano({ ...aMano, semana: e.target.value })}
                                    className="mt-1 w-full bg-[#0A0A0A] border border-[#2A2A2A] rounded-md px-2 py-1.5 text-sm text-white" />
                            </label>
                        </div>
                        <div className="flex gap-2 justify-end">
                            <Button onClick={() => setAMano(null)} className="bg-transparent border border-[#2A2A2A] text-white/70 h-9">Cancelar</Button>
                            <Button onClick={fijar} disabled={trabajando} className="bg-[#FF671F] hover:bg-[#FF671F]/90 text-white h-9" data-testid="paso-fijar">Poner</Button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SimuladorPasoAPaso;
